// src/pages/ReferralTrackingPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/useRedux';
import { getBaseUrl } from '../config/api';
import { appLinks } from '../config/appLinks';
import { AppleStoreIcon, GooglePlayIcon } from '../components/common/StoreBrandIcons';
import { hasStoredConsent } from '../utils/consentAwareLoader';

/**
 * Referral Tracking Page
 * Handles affiliate referral links: /ref/:referralCode
 * Tracks the click and shows app download options for mobile users
 */
const ReferralTrackingPage: React.FC = () => {
  const { referralCode } = useParams<{ referralCode: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAppSelector((state) => state.auth);
  const [isTracking, setIsTracking] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileOptions, setShowMobileOptions] = useState(false);
  const [affiliateName, setAffiliateName] = useState<string>('');

  // Detect if user is on mobile
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
    const isMobileDevice = mobileKeywords.some(keyword => userAgent.includes(keyword));
    setIsMobile(isMobileDevice);
  }, []);

  useEffect(() => {
    const trackReferral = async () => {
      if (!referralCode) {
        setError('Invalid referral code');
        setIsTracking(false);
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      try {
        // Call the tracking endpoint
        // Use same JSON endpoint as mobile (`mobile-track`), not `/track` (redirect/HTML for browsers).
        const trackingUrl = `${getBaseUrl()}/v1/mobile-track/${encodeURIComponent(referralCode)}`;
        
        // Get user's IP and user agent for tracking
        const response = await fetch(trackingUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include' // Include cookies for session tracking
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // Same storage keys as mobile AsyncStorage (@affiliate_*); normalize code like backend.
          const codeUpper = (referralCode || '').toUpperCase();
          sessionStorage.setItem('affiliateReferralCode', codeUpper);
          sessionStorage.setItem('affiliateClickId', data.data?.clickId || '');
          sessionStorage.setItem('affiliateSessionId', data.data?.sessionId || '');
          
          // Store affiliate name for display
          if (data.data?.affiliate?.name) {
            setAffiliateName(data.data.affiliate.name);
          }
          
          // Set persistent cookie if functional consent is already granted.
          // For new visitors (no consent yet), store the code as a pending intent so
          // App.tsx can write the cookie once the user acts on the consent banner.
          if (hasStoredConsent('functional')) {
            const expiryDate = new Date();
            expiryDate.setDate(expiryDate.getDate() + 30);
            document.cookie = `affiliateReferralCode=${codeUpper}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
          } else {
            sessionStorage.setItem('pendingAffiliateCookieCode', codeUpper);
          }
          
          console.log('Referral tracked successfully:', {
            referralCode: codeUpper,
            clickId: data.data?.clickId,
            sessionId: data.data?.sessionId
          });
        } else {
          console.warn('Tracking failed, but continuing:', data.message || 'Unknown error');
          // Still store the referral code even if tracking fails
          sessionStorage.setItem('affiliateReferralCode', (referralCode || '').toUpperCase());
        }
      } catch (error) {
        console.error('Error tracking referral:', error);
        // Still store the referral code even if tracking fails
        sessionStorage.setItem('affiliateReferralCode', (referralCode || '').toUpperCase());
      } finally {
        setIsTracking(false);
        
        // Check if user is on mobile - show app download options
        if (isMobile) {
          setShowMobileOptions(true);
          return;
        }
        
        // Check if user is logged in
        const isLoggedIn = token || localStorage.getItem('token') || sessionStorage.getItem('token');
        
        if (!isLoggedIn) {
          // User is not logged in - redirect to registration with referral code
          // The referral code is already stored in sessionStorage
          const redirectPath = searchParams.get('redirect');
          navigate('/register', {
            state: {
              referralCode,
              redirect: redirectPath || '/client/dashboard',
              message: 'Please create an account to continue with your referral link'
            },
            replace: true
          });
        } else {
          // User is already logged in - redirect to homepage or specified path
          const redirectPath = searchParams.get('redirect') || '/';
          navigate(redirectPath, { replace: true });
        }
      }
    };

    trackReferral();
  }, [referralCode, searchParams, navigate, token, user, isMobile]);

  if (isTracking) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #C9A84C 0%, #A8872A 100%)',
        color: 'white'
      }}>
        <div className="spinner" style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(255, 255, 255, 0.3)',
          borderTopColor: 'white',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '1rem'
        }}></div>
        <p style={{ fontSize: '1.1rem', fontWeight: 500 }}>Tracking referral...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f7fafc',
        color: '#2d3748'
      }}>
        <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>{error}</p>
        <p style={{ fontSize: '0.9rem', color: '#718096' }}>Redirecting to homepage...</p>
      </div>
    );
  }

  // Show mobile app download options
  if (showMobileOptions) {
    const appStoreUrl = appLinks.ios;
    const playStoreUrl = appLinks.android;
    
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #C9A84C 0%, #A8872A 100%)',
        color: 'white',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '400px',
          width: '100%'
        }}>
          {/* Logo placeholder */}
          <div style={{
            width: '80px',
            height: '80px',
            background: 'white',
            borderRadius: '20px',
            margin: '0 auto 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
          }}>
            <span style={{ fontSize: '2rem' }}>💇</span>
          </div>
          
          <h1 style={{ 
            fontSize: '1.8rem', 
            fontWeight: '700', 
            marginBottom: '0.5rem' 
          }}>
            Welcome to LookBel!
          </h1>
          
          {affiliateName && (
            <p style={{ 
              fontSize: '1rem', 
              opacity: 0.9,
              marginBottom: '1.5rem'
            }}>
              Referred by <strong>{affiliateName}</strong>
            </p>
          )}
          
          <p style={{ 
            fontSize: '1rem', 
            opacity: 0.8,
            marginBottom: '2rem',
            lineHeight: 1.6
          }}>
            Download our app to book amazing beauty services from top stylists in your area.
          </p>
          
          {/* Referral code display */}
          <div style={{
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '2rem'
          }}>
            <p style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.5rem' }}>
              Your referral code
            </p>
            <p style={{ 
              fontSize: '1.5rem', 
              fontWeight: '700',
              letterSpacing: '2px'
            }}>
              {referralCode}
            </p>
            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.5rem' }}>
              Enter this code during registration
            </p>
          </div>
          
          {/* App Store buttons */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <ReferralStoreAction
              href={appStoreUrl}
              label="Download on the App Store"
              icon={<AppleStoreIcon />}
            />
            <ReferralStoreAction
              href={playStoreUrl}
              label="Get it on Google Play"
              icon={<GooglePlayIcon />}
            />
          </div>
          
          {/* Continue on web option */}
          <button
            onClick={() => {
              navigate('/register', {
                state: {
                  referralCode,
                  redirect: '/client/dashboard',
                  message: 'Please create an account to continue with your referral link'
                },
                replace: true
              });
            }}
            style={{
              background: 'transparent',
              border: '2px solid rgba(255,255,255,0.5)',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: '0.95rem',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            Continue on Web Instead
          </button>
        </div>
      </div>
    );
  }

  // This shouldn't be reached, but just in case
  return null;
};

const referralStoreStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.75rem',
  background: 'white',
  color: '#1a202c',
  padding: '1rem 1.5rem',
  borderRadius: '12px',
  textDecoration: 'none',
  fontWeight: 600,
  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
  border: 'none',
  width: '100%',
  font: 'inherit',
};

function ReferralStoreAction({
  href,
  label,
  icon,
}: {
  href: string | null;
  label: string;
  icon: React.ReactNode;
}) {
  if (!href) {
    return (
      <button type="button" disabled style={{ ...referralStoreStyle, background: '#f3f4f6', color: '#6b7280', cursor: 'default' }}>
        {icon}
        Coming Soon
      </button>
    );
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={referralStoreStyle}>
      {icon}
      {label}
    </a>
  );
}

export default ReferralTrackingPage;

